import React from 'react';
import { Link } from 'react-router-dom';

import {
  finleyAssert,
  finleyAssertUnreachable,
} from '@finley-technologies/common';

import { getCustomError as limitedPartnersError } from './LimitedPartners/LimitedPartnersCustomError';
import { getCustomError as receivableReconciliationError } from './ReceivableReconciliation/ReceivableReconciliation';
import { useUploadContext } from '~/packages/dataPipeline/hooks/UploadContext';
import { ActionableAlert } from '~/packages/dataPipeline/ui/components/ActionableAlert';
import { PipelineDetailsPageLink } from '~/packages/dataPipeline/ui/uploads/PipelineDetailsPageLink';

export const UploadDrawerAlert = () => {
  const {
    canUpload,
    completeRecord,
    meshRunId,
    instances,
  } = useUploadContext();

  if (!canUpload) {
    return (
      <ActionableAlert status="info" mb="10px">
        Pipeline changed. You can not upload files to this pipeline.
      </ActionableAlert>
    );
  }

  if (!meshRunId) {
    return null;
  }

  // since mesRunId is available we can assume that completeRecord is available
  finleyAssert(completeRecord, 'completeRecord must be available');
  const status = completeRecord.meshRunRecord.status;
  const pipelineLinkElement = <PipelineDetailsPageLink meshRunId={meshRunId} />;
  switch (status) {
    case 'inProgress':
      return (
        <ActionableAlert
          status="info"
          mb="10px"
          actionElement={pipelineLinkElement}
        >
          Pipeline in progress
        </ActionableAlert>
      );

    case 'error': {
      // check if we need custom error for receivable reconciliation
      // or limited partners
      const customError = receivableReconciliationError(
        completeRecord.meshDocument.artifacts,
        instances,
      ) ||
        limitedPartnersError({
          artifacts: completeRecord.meshDocument.artifacts,
          instances,
        }) || {
          customMessage: 'There are errors in the pipeline.',
        };
      return (
        <ActionableAlert
          status="error"
          mb="10px"
          actionElement={customError.actionElement || pipelineLinkElement}
        >
          {customError.customMessage}
        </ActionableAlert>
      );
    }

    case 'success':
      return null;

    case 'waitingForInput':
      return (
        <ActionableAlert
          status="warning"
          mb="10px"
          actionElement={pipelineLinkElement}
        >
          Upload all required files
        </ActionableAlert>
      );

    case 'waitingForAssignment': {
      const assignmentLinkElement = (
        <Link
          to="/assignments"
          style={{ color: 'blue', textDecoration: 'underline' }}
        >
          Confirm Assignment
        </Link>
      );

      return (
        <ActionableAlert status="warning" actionElement={assignmentLinkElement}>
          Review your assignments.
        </ActionableAlert>
      );
    }

    case 'waitingForVerificationApproval':
      return (
        <ActionableAlert status="warning">Waiting for approval</ActionableAlert>
      );

    case 'expired':
      return (
        <ActionableAlert status="warning">
          This pipeline is expired.
        </ActionableAlert>
      );
    default:
      return finleyAssertUnreachable(status);
  }
};
